import {
  Box,
  Button,
  IconButton,
  lighten,
  Stack,
  Tooltip,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Tooltip as MuiTooltip } from "@mui/material";
import * as React from "react";
import { useState } from "react";
import { useAmplitudeTrack, setSiteSelectionSource, setSitesCount, getSitesCount, SITE_SELECTION_SOURCE } from "../../scripts/amplitude_utils";

import useBackend, { useUserPrivilegeKeyPermissionsMap } from "../../hooks/use_backend";
import {
  addOwnership,
  fetchSites,
  renameSite,
  replaceBox,
  updateFavourite,
} from "../../scripts/api_v2/site_client";
import { fetchAllActiveSiteAlertMutes } from "../../scripts/api_v2/alerting_client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";
import ReactTableWithSettings from "../../components/material_react_table/react_table_with_settings";
import { REACT_TABLE_NAMES } from "../../enums/react_table_name_enum";
import ErrorIcon from "@mui/icons-material/Warning";
import { AlertColors } from "../../components/alerting/alert_enums";
import WarningIcon from "@mui/icons-material/Report";
import InfoIcon from "@mui/icons-material/Info";
import CheckIcon from "@mui/icons-material/Check";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import BugReportIcon from "@mui/icons-material/BugReport";
import { toDate } from "../../scripts/utils";
import { useLocation, useNavigate } from "react-router-dom";
import { NAVIGATION_PATH } from "../../enums/navigation_enum";
import DialogForm from "../../components/material_react_table/dialog_form";
import { FORM_ACTIONS } from "../../enums/form_actions_enum";
import MultiTabDialog from "../../components/material_react_table/multi_tab_dialog";
import Snackbar from "../../scripts/snackbar";
import { Star, StarBorder } from "@mui/icons-material";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import NotificationsIcon from "@mui/icons-material/Notifications";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { themeOptions as theme } from "../../app";
import { get_status_for_action } from "./tabs/commissioning_tab/components/enum/commissioning_required_action";
import { hasPermission } from "../login/login_utils";
import Privilege from "../../enums/privilege_enum";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import { createNewInstallation } from "../../scripts/api_v2/crm_client";
import { TOOLTIPS_EN } from "../../enums/tooltip_enum_EN";

function toUnicodeCheck(value) {
  return value ? "\u2713" : ""; //"\u2715";
}

function AlertFilterButton({ label, column, onManualToggle, children }) {
  function toggleFilter() {
    onManualToggle?.();
    const filterValue = column.getFilterValue() ? column.getFilterValue() : [];
    const labelIdx = filterValue.indexOf(label);
    if (labelIdx === -1) {
      column.setFilterValue((prev) => {
        prev = prev ? prev : [];
        return [...prev, label];
      });
    } else {
      column.setFilterValue((prev) => prev.filter((_, idx) => idx !== labelIdx));
    }
  }

  return <IconButton onClick={toggleFilter}>{children}</IconButton>;
}

function generateFilterIcons(column, filterValue, theme, renderActive, onManualToggle) {
  const filterLabels = ["critical", "error", "warning", "info", "pending", "status ok"];
  const icons = [];

  filterLabels.forEach((label) => {
    const isActive = filterValue.includes(label);
    const color = isActive
      ? AlertColors[label.toUpperCase().replace(" ", "_")] || AlertColors.SUCCESS
      : lighten(AlertColors[label.toUpperCase().replace(" ", "_")] || AlertColors.SUCCESS, 0.8);
    const Icon =
      label === "critical"
        ? ErrorIcon
        : label === "error"
          ? ErrorIcon
          : label === "warning"
            ? WarningIcon
            : label === "info"
              ? InfoIcon
              : label === "pending"
                ? HourglassEmptyIcon
                : CheckIcon;

    if (renderActive && isActive) {
      icons.push(
        <AlertFilterButton key={label} label={label} column={column} onManualToggle={onManualToggle}>
          <Tooltip title={label.charAt(0).toUpperCase() + label.slice(1)}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Icon htmlColor={color} />
            </Stack>
          </Tooltip>
        </AlertFilterButton>,
      );
    } else if (!renderActive && !isActive) {
      icons.push(
        <AlertFilterButton key={label} label={label} column={column} onManualToggle={onManualToggle}>
          <Tooltip title={label.charAt(0).toUpperCase() + label.slice(1)}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Icon htmlColor={color} />
            </Stack>
          </Tooltip>
        </AlertFilterButton>,
      );
    }
  });

  return icons;
}

function AlertFilterSection({ column, onManualToggle }) {
  const theme = useTheme();
  const filterValue = column.getFilterValue() || [];
  const allFiltersActive = filterValue.length === 6; // All filters are active if 6 filters are selected

  const inactiveIcons = generateFilterIcons(column, filterValue, theme, false, onManualToggle);

  const activeIcons = generateFilterIcons(column, filterValue, theme, true, onManualToggle);

  return (
    <Stack direction="row" alignItems="center" display="flex" justifyContent={"flex-start"}>
      {inactiveIcons}
      {filterValue.length > 0 && !allFiltersActive && (
        <span
          style={{
            height: "24px",
            width: "2px",
            backgroundColor: "gray",
            margin: "0 8px",
          }}
        />
      )}
      {activeIcons}
    </Stack>
  );
}

function connectionColor(online, ping, v) {
  if (online === "nan") return "#fa4670";
  else if (online) return v.split("-").length === 2 ? "#196322" : "#37db4a";
  if (online === "nan" || online === null) return "#fa4670";
  else if (online) {
    if (v === "nan" || v === null) return "#37db4a";
    else return v.split("-").length === 2 ? "#196322" : "#37db4a";
  }
  //else if (curr_ts - 1000 * ping > 1000 * 60 * 60 * 24 * 2) return "#e0e0e0" // longer than one day offline

  const curr_ts = new Date().getTime();
  // red criterion
  if (1000 * ping > curr_ts - 1000 * 60 * 60 * 24) return v.split("-").length === 2 ? "#a1203e" : "#fa4670";
  else if (1000 * ping > curr_ts - 1000 * 60 * 60 * 24 * 7) return v.split("-").length === 2 ? "#db8802" : "#fcad2d";
  if (1000 * ping > curr_ts - 1000 * 60 * 60 * 24) {
    if (v === "nan" || v === null) {
      return "#fa4670";
    } else {
      return v.split("-").length === 2 ? "#a1203e" : "#fa4670";
    }
  } else if (1000 * ping > curr_ts - 1000 * 60 * 60 * 24 * 7) {
    if (v === "nan" || v === null) {
      return "#fcad2d";
    } else {
      return v.split("-").length === 2 ? "#db8802" : "#fcad2d";
    }
  } else return "#e0e0e0"; // longer than two days offline
}

const STATUS_DISPLAY_CONFIG = new Map([
  ["healthy", { Icon: CheckIcon, color: AlertColors.SUCCESS }],
  ["debug", { Icon: BugReportIcon, color: AlertColors.DEBUG }],
  ["critical", { Icon: ErrorIcon, color: AlertColors.CRITICAL }],
  ["error", { Icon: ErrorIcon, color: AlertColors.ERROR }],
  ["warning", { Icon: WarningIcon, color: AlertColors.WARNING }],
  ["info", { Icon: InfoIcon, color: AlertColors.INFO }],
  ["pending", { Icon: HourglassEmptyIcon, color: AlertColors.PENDING }],
]);

function capitalize(text) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function resolveRowStatus(row) {
  if (row?.is_debug) return "debug";
  return typeof row?.status === "string" ? row.status.toLowerCase() : null;
}

function buildStatusTooltip(status, defaultTooltip, alerts) {
  if (status === "healthy" || status === "debug" || !Array.isArray(alerts)) {
    return defaultTooltip;
  }

  const alertMessages = alerts.map((alert) => alert?.info).filter(Boolean);
  return alertMessages.length > 0 ? alertMessages.join("\n") : defaultTooltip;
}

function getStatusDisplay(row) {
  const status = resolveRowStatus(row);
  const config = STATUS_DISPLAY_CONFIG.get(status);
  if (!config) return null;

  return {
    Icon: config.Icon,
    color: config.color,
    tooltip: buildStatusTooltip(status, capitalize(status), row?.alerts),
  };
}

export default function SitesOverview({ required_privilege = null }) {
  const navigate = useNavigate();
  const backend = useBackend();
  const { trackEvent, trackPageLoaded } = useAmplitudeTrack();
  const permissionMap = useUserPrivilegeKeyPermissionsMap();
  const location = useLocation();
  const locationContextFilters = location.state && location.state.filters ? location.state.filters : null;
  const locationContextSorting = location.state && location.state.sorting ? location.state.sorting : null;
  const isPrimaryAlertFilterRef = React.useRef(
    Boolean(locationContextFilters?.some((filter) => filter.id === "status")),
  );
  const pageLoadStartTime = React.useRef(performance.now());

  React.useEffect(() => {
    trackEvent('sites_overview_viewed');
  }, [trackEvent]);

  const muteViewSessionStorageKey = "sites_overview_mute_view";

  const getInitialMuteView = () => {
    const persistedMuteView = sessionStorage.getItem(muteViewSessionStorageKey);
    if (persistedMuteView) return persistedMuteView;
    const includeMutes = location.state?.includeMutes;
    if (includeMutes === undefined) return "normal";
    if (typeof includeMutes === "boolean") return includeMutes ? "normal" : "mutes";
    return includeMutes;
  };

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogTabs, setDialogTabs] = useState(null);
  const [muteView, setMuteView] = useState(getInitialMuteView());
  const queryClient = useQueryClient();
  const globalFilterSessionStorageKey = "sites_overview_global_filter";
  const [globalFilterValue, setGlobalFilterValue] = useState(
    () => sessionStorage.getItem(globalFilterSessionStorageKey) || "",
  );

  const handleGlobalFilterChange = (newGlobalFilterValue) => {
    if (newGlobalFilterValue) {
      trackEvent('sites_search_used', {
        search_query: newGlobalFilterValue,
        ...(getSitesCount() !== null ? { sites_count: getSitesCount() } : {}),
      });
    }
    setGlobalFilterValue(newGlobalFilterValue);
    // Clear persisted value as soon as user edits search; it will be re-saved only on site navigation.
    sessionStorage.removeItem(globalFilterSessionStorageKey);
    localStorage.removeItem(globalFilterSessionStorageKey);
  };

  const handleToggleChange = (event, newValue) => {
    if (newValue !== null) {
      setMuteView(newValue);
      sessionStorage.setItem(muteViewSessionStorageKey, newValue);
    }
  };

  const toggleButton = (
    <ToggleButtonGroup
      value={muteView}
      exclusive
      onChange={handleToggleChange}
      size="small"
      aria-label="alert view toggle"
    >
      <MuiTooltip title="Sites without mutes" arrow>
        <ToggleButton value="normal" aria-label="normal alerts" sx={{ px: 1, py: 0.5 }}>
          <NotificationsIcon sx={{ fontSize: 16 }} />
        </ToggleButton>
      </MuiTooltip>
      <MuiTooltip title="Sites with mutes" arrow>
        <ToggleButton value="mutes" aria-label="include mutes" sx={{ px: 1, py: 0.5 }}>
          <NotificationsOffIcon sx={{ fontSize: 16 }} />
        </ToggleButton>
      </MuiTooltip>
      <MuiTooltip title="List of all mutes" arrow>
        <ToggleButton value="allMutes" aria-label="all mutes" sx={{ px: 1, py: 0.5 }}>
          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        </ToggleButton>
      </MuiTooltip>
    </ToggleButtonGroup>
  );

  const activeViewSessionStorageKey = "sites_overview_active_view";

  const getInitialActiveView = () => {
    const persistedActiveView = sessionStorage.getItem(activeViewSessionStorageKey);
    return persistedActiveView || "active";
  };

  const [activeView, setActiveView] = useState(getInitialActiveView());

  React.useEffect(() => {
    if (locationContextFilters) {
      setActiveView("active");
      sessionStorage.setItem(activeViewSessionStorageKey, "active");
    }
  }, [locationContextFilters]);

  const handleActiveViewChange = (event, newValue) => {
    if (newValue !== null) {
      setActiveView(newValue);
      sessionStorage.setItem(activeViewSessionStorageKey, newValue);
    }
  };

  const activeFilterValue = activeView === "active" ? true : activeView === "inactive" ? false : undefined;

  const externalColumnFilters = React.useMemo(
    () => [{ id: "is_active", value: activeFilterValue }],
    [activeFilterValue],
  );

  const activeToggleButton = (
    <ToggleButtonGroup
      value={activeView}
      exclusive
      onChange={handleActiveViewChange}
      size="small"
      aria-label="active view toggle"
    >
      <MuiTooltip title="List active sites" arrow>
        <ToggleButton value="active" aria-label="only active sites" sx={{ px: 1, py: 0.5 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
        </ToggleButton>
      </MuiTooltip>
      <MuiTooltip title="List inactive sites" arrow>
        <ToggleButton value="inactive" aria-label="only inactive sites" sx={{ px: 1, py: 0.5 }}>
          <HighlightOffIcon sx={{ fontSize: 16 }} />
        </ToggleButton>
      </MuiTooltip>
      <MuiTooltip title="List all sites" arrow>
        <ToggleButton value="all" aria-label="all sites" sx={{ px: 1, py: 0.5 }}>
          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        </ToggleButton>
      </MuiTooltip>
    </ToggleButtonGroup>
  );

  const handleClearFilters = () => {
    setActiveView("all");
    sessionStorage.setItem(activeViewSessionStorageKey, "all");
  };

  const toolbarToggleButtons = (
    <Stack direction="row" spacing={1} alignItems="center">
      {toggleButton}
      {activeToggleButton}
    </Stack>
  );

  const { data, error, isLoading } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_OVERVIEW, muteView],
    queryFn: async () => {
      const sites = await fetchSites(backend.fetch, muteView !== "mutes");

      if (muteView === "allMutes") {
        // Fetch all active mutes and filter sites to only those with active mutes
        const allMutes = await fetchAllActiveSiteAlertMutes(backend.fetch);
        const sitesWithMutes = new Set(allMutes.map((mute) => mute.site_id));
        return sites.filter((site) => sitesWithMutes.has(site.site_id));
      }

      return sites;
    },
  });

  const pageLoadTracked = React.useRef(false);
  React.useEffect(() => {
    if (!isLoading && data) {
      setSitesCount(data.length || 0);
      // only the first load counts as page load; later refetches (mute toggle, polling) must not re-report
      if (pageLoadTracked.current) return;
      pageLoadTracked.current = true;
      const loadTime = performance.now() - pageLoadStartTime.current;
      trackPageLoaded('sites_overview', loadTime, {
        sites_count: data.length || 0,
      });
    }
  }, [isLoading, data, trackPageLoaded]);

  const replaceBoxMutation = useMutation({
    mutationFn: async (data) => await replaceBox(backend.fetch, data.siteId, data.boxId),
    onSuccess: () => {
      Snackbar.success("Box replacement success");
    },
    onError: (err, data, context) => {
      Snackbar.error("Box replacement failed: " + err);
    },
  });

  const renameSiteMutation = useMutation({
    mutationFn: async (data) => await renameSite(backend.fetch, data.siteId, data.siteName),
    onSuccess: (newData, variables) => {
      Snackbar.success("Renaming site success");
      queryClient.setQueryData([REACT_QUERY_KEYS.SITE_OVERVIEW, muteView], (oldData) => {
        if (!oldData) return oldData; // Ensure old data exists else return nothing
        return oldData.map(
          (site) => (site.site_id === variables.siteId ? { ...site, site_name: variables.siteName } : site), // Keep other items unchanged
        );
      });
    },
    onError: (err, data, context) => {
      Snackbar.error("Renaming site failed: " + err);
    },
  });

  const { mutate: doFavouriteMutation } = useMutation({
    mutationFn: async ({ siteId }) => {
      return await updateFavourite(backend.fetch, siteId);
    },
    onSuccess: (newData, variables) => {
      Snackbar.success("Updating site success");
      queryClient.setQueryData([REACT_QUERY_KEYS.SITE_OVERVIEW, muteView], (oldData) => {
        if (!oldData) return oldData; // Ensure old data exists else return nothing
        return oldData.map(
          (site) =>
            site.site_id === variables.siteId ? { ...site, is_favourite_site: !site.is_favourite_site } : site, // Keep other items unchanged
        );
      });
    },
    onError: (err, data, context) => {
      Snackbar.error("Updating site failed: " + err);
    },
  });

  const addOwnerMutation = useMutation({
    mutationFn: async (data) => await addOwnership(backend.fetch, data.siteId, data.partnerCompanyId),
    onSuccess: () => {
      Snackbar.success("Owner was added");
    },
    onError: (err, data, context) => {
      Snackbar.error("Adding owner failed: " + err);
    },
  });

  function sanitizeData(data) {
    if (data === null || data === undefined) {
      return [];
    }
    if (!Array.isArray(data) || data.length === 0) {
      return [];
    } else {
      const allKeys = Object.keys(data[0]);
      const booleanKeys = [];
      const dateKeys = [];
      allKeys.forEach((key) => {
        const lowerKey = key.toLowerCase();
        // Track all columns keys that need to be cast to a bool value
        if (lowerKey.includes("has_") || lowerKey === "commissioning_completed" || lowerKey.includes("is_")) {
          booleanKeys.push(key);
        }
        // Track all columns keys that need to be cast to a date value
        if (lowerKey.includes("date")) {
          dateKeys.push(key);
        }
      });
      // Do the actual sanitizing
      return data.map((row) => {
        const sanitizedRow = { ...row };
        booleanKeys.forEach((key) => {
          sanitizedRow[key] = sanitizedRow[key] === null || sanitizedRow[key] === "" ? false : sanitizedRow[key];
        });
        dateKeys.forEach((key) => {
          sanitizedRow[key] = toDate(sanitizedRow[key]);
        });
        if (!sanitizedRow["commissioning_completed"]) {
          sanitizedRow["site_quality_rating"] = null;
        }
        sanitizedRow["commissioning_required_action"] = get_status_for_action(
          sanitizedRow["commissioning_required_action"],
        );
        sanitizedRow["ean_injection_street"] =
          !sanitizedRow["ean_injection_street"] && !sanitizedRow["ean_injection_city"]
            ? ""
            : `${sanitizedRow["ean_injection_street"]} ${sanitizedRow["ean_injection_street_number"]}, 
                    ${sanitizedRow["ean_injection_postal_code"]}, ${sanitizedRow["ean_injection_city"]}`;
        sanitizedRow["ean_offtake_street"] =
          !sanitizedRow["ean_offtake_street"] && !sanitizedRow["ean_offtake_city"]
            ? ""
            : `${sanitizedRow["ean_offtake_street"]} ${sanitizedRow["ean_offtake_street_number"]}, 
                    ${sanitizedRow["ean_offtake_postal_code"]}, ${sanitizedRow["ean_offtake_city"]}`;
        return sanitizedRow;
      });
    }
  }

  const sanitizedData = React.useMemo(() => sanitizeData(data), [data]);

  const defaultVisibility = {
    box_id: true,
    site_name: true,
    status: true,
    site_quality_rating: true,
    commissioning_completed: true,
    total_curtailable_inverter_power: true,
    total_controlable_battery_power: true,
    ean_offtake_street: true,
    site_id: false,
    elion_name: false,
    cl_version: false,
    owners: false,
    ean_offtake: false,
    ean_injection: false,
    has_active_offtake: false,
    has_active_injection: false,
    is_active: false,
    average_monthly_profit: false,
    profit_per_kw: false,
    account_name_offtake: false,
    account_name_injection: false,
    account_vat_offtake: false,
    account_vat_injection: false,
    commissioning_completed_date_utc: false,
    active_verification_mod_date: false,
    commissioning_required_action: false,
    has_devices_in_commissioning: false,
    ean_injection_street: false,
    is_cap_floor: false,
    is_solar_curtailment: false,
    total_uncurtailable_inverter_power: false,
    total_controlable_power: false,
    capacity: false,
    has_curtailable_devices: false,
    nb_devices: false,
    act_optimizer: true,
    conf_optimizer: true,
  };

  const defaultSorting = [{ id: "site_quality_rating", desc: false }];

  const columns = React.useMemo(
    () => [
      {
        accessorKey: "is_favourite_site",
        header: "FAVOURITES",
        enableGlobalFilter: false,
        filterVariant: "checkbox",
        Cell: ({ cell }) =>
          cell.getValue() ? (
            <Star
              style={{ color: theme.palette.primary.main }}
              onClick={() => doFavouriteMutation({ siteId: cell.row.original.site_id })}
            />
          ) : (
            <StarBorder
              sx={{ color: "gray", transition: "color 0.3s", "&:hover": { color: theme.palette.primary.main } }}
              onClick={() => doFavouriteMutation({ siteId: cell.row.original.site_id })}
            />
          ),
      },
      { accessorKey: "box_id", header: "BOX ID" },
      { accessorKey: "site_name", header: "SITE NAME" },
      {
        accessorKey: "status",
        header: "STATUS",
        enableGlobalFilter: false,
        Cell: ({ row }) => {
          const display = getStatusDisplay(row.original);
          if (!display) return null;

          return (
            <Tooltip
              title={
                <Box sx={{ whiteSpace: "pre-line" }}>
                  {display.tooltip}
                </Box>
              }
            >
              <display.Icon htmlColor={display.color} />
            </Tooltip>
          );
        },
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue || filterValue.length === 0) return true;
          const rawStatus = row.getValue(columnId);
          const status = !rawStatus || rawStatus === "healthy" ? "status ok" : rawStatus;
          return filterValue.includes(status);
        },
        filterVariant: "custom",
        Filter: (props) => (
          <AlertFilterSection {...props} onManualToggle={() => (isPrimaryAlertFilterRef.current = false)} />
        ),
      },
      {
        accessorKey: "site_quality_rating",
        header: "SITE QUALITY RATING",
        enableGlobalFilter: false,
        filterVariant: "range",
        filterFn: "customNumberPercentageInRange",
        Cell: ({ cell }) => {
          let backgroundColor;
          if (cell.getValue() == null || isNaN(cell.getValue())) {
            return null;
          } else {
            if (cell.getValue() >= 0.75) {
              backgroundColor = "green";
            } else if (cell.getValue() >= 0.6 && cell.getValue() < 0.75) {
              backgroundColor = "orange";
            } else if (cell.getValue() < 0.6) {
              backgroundColor = "red";
            }

            return <b style={{ color: backgroundColor }}>{`${(cell.getValue() * 100).toFixed(0)}%`}</b>;
          }
        },
      },
      {
        accessorKey: "total_curtailable_inverter_power",
        header: "TOTAL CURTAILABLE INVERTER POWER",
        enableGlobalFilter: false,
        filterVariant: "range",
        filterFn: "customScaledKiloUnitInRange",
        Cell: ({ row }) => {
          const curtInvPow = row.original.total_curtailable_inverter_power;
          return curtInvPow != null && !isNaN(curtInvPow) ? `${(curtInvPow / 1000).toFixed(2)}kW` : curtInvPow;
        },
      },
      {
        accessorKey: "total_controlable_battery_power",
        header: "TOTAL CONTROLABLE BATTERY POWER",
        enableGlobalFilter: false,
        filterVariant: "range",
        filterFn: "customScaledKiloUnitInRange",
        Cell: ({ row }) => {
          const conBatPow = row.original.total_controlable_battery_power;
          return conBatPow != null && !isNaN(conBatPow) ? `${(conBatPow / 1000).toFixed(2)}kW` : conBatPow;
        },
      },
      { accessorKey: "elion_name", header: "ELION CONTRACT NAME" },
      {
        accessorKey: "cl_version",
        header: "VERSION",
        enableGlobalFilter: false,
        Cell: ({ row }) => (
          <b
            style={{
              backgroundColor: connectionColor(row.original.online, row.original.ping, row.original.cl_version),
              padding: "4px 4px",
              borderRadius: 5,
              color: "white",
              fontSize: 12,
            }}
          >
            v{row.original.cl_version ? row.original.cl_version : "3.0.0"}
          </b>
        ),
      },
      { accessorKey: "owners", header: "SITE OWNERS" },
      { accessorKey: "ean_offtake", header: "OFFTAKE EAN" },
      { accessorKey: "ean_injection", header: "INJECTION EAN" },
      {
        accessorKey: "has_active_offtake",
        header: "OFFTAKE CONTRACT ACTIVE",
        enableGlobalFilter: false,
        filterVariant: "checkbox",
        Cell: ({ cell }) => toUnicodeCheck(cell.getValue()),
      },
      {
        accessorKey: "has_active_injection",
        header: "INJECTION CONTRACT ACTIVE",
        enableGlobalFilter: false,
        filterVariant: "checkbox",
        Cell: ({ cell }) => toUnicodeCheck(cell.getValue()),
      },
      {
        accessorKey: "is_active",
        header: "ACTIVE",
        enableGlobalFilter: false,
        Cell: ({ cell }) => toUnicodeCheck(cell.getValue()),
        filterFn: "triStateBooleanFilter",
        enableColumnFilter: false,
        enableHiding: true,
      },
      { accessorKey: "site_id", header: "SITE ID" },
      {
        accessorKey: "average_monthly_profit",
        header: "AVERAGE MONTHLY PROFIT",
        enableGlobalFilter: false,
        filterFn: "inNumberRange",
        Cell: ({ row }) => {
          const avgProfit = row.original.average_monthly_profit;
          return avgProfit != null && !isNaN(avgProfit)
            ? `€${avgProfit.toFixed(2)}` // Round to 2 decimals and add the Euro sign
            : avgProfit;
        },
      },
      {
        accessorKey: "profit_per_kw",
        header: "PROFIT PER KW",
        enableGlobalFilter: false,
        filterFn: "inNumberRange",
        Cell: ({ row }) => {
          const profitPer = row.original.profit_per_kw;
          return profitPer != null && !isNaN(profitPer)
            ? `€${profitPer.toFixed(2)}` // Round to 2 decimals and add the Euro sign
            : profitPer;
        },
      },
      { accessorKey: "account_name_offtake", header: "OFFTAKE ACCOUNT" },
      { accessorKey: "account_name_injection", header: "INJECTION ACCOUNT" },
      { accessorKey: "account_vat_offtake", header: "OFFTAKE VAT" },
      { accessorKey: "account_vat_injection", header: "INJECTION VAT" },
      { accessorKey: "ean_injection_street", header: "INJECTION METER ADDRESS" },
      { accessorKey: "ean_offtake_street", header: "OFFTAKE METER ADDRESS" },
      {
        accessorKey: "commissioning_completed",
        header: "COMMISSIONING COMPLETED",
        enableGlobalFilter: false,
        filterVariant: "checkbox",
        Cell: ({ cell }) => toUnicodeCheck(cell.getValue()),
      },
      {
        accessorKey: "commissioning_completed_date_utc",
        header: "COMMISSIONING COMPLETED DATE",
        enableGlobalFilter: false,
        filterVariant: "datetime-range",
        Cell: ({ cell }) => (cell.getValue() ? cell.getValue().toLocaleString() : null),
        sortingFn: "customDateSortingFn",
        filterFn: "customDatetimeRangeFilter",
      },
      {
        accessorKey: "active_verification_mod_date",
        header: "ACTIVE VERIFCATION MOD DATE",
        enableGlobalFilter: false,
        filterVariant: "datetime-range",
        Cell: ({ cell }) => (cell.getValue() ? cell.getValue().toLocaleString() : null),
        sortingFn: "customDateSortingFn",
        filterFn: "customDatetimeRangeFilter",
      },
      {
        accessorKey: "has_devices_in_commissioning",
        header: "HAS DEVICES IN COMMISSIONING",
        enableGlobalFilter: false,
        filterVariant: "checkbox",
        Cell: ({ cell }) => toUnicodeCheck(cell.getValue()),
      },
      { accessorKey: "commissioning_required_action", header: "COMMISSIONING REQUIRED ACTION" },
      {
        accessorKey: "is_cap_floor",
        header: "PRODUCT CAP FLOOR",
        enableGlobalFilter: false,
        filterVariant: "checkbox",
        Cell: ({ cell }) => toUnicodeCheck(cell.getValue()),
      },
      {
        accessorKey: "is_solar_curtailment",
        header: "PRODUCT SOLAR CURTAILMENT",
        enableGlobalFilter: false,
        filterVariant: "checkbox",
        Cell: ({ cell }) => toUnicodeCheck(cell.getValue()),
      },
      {
        accessorKey: "total_uncurtailable_inverter_power",
        header: "TOTAL UNCURTAILABLE INVERTER POWER",
        enableGlobalFilter: false,
        filterVariant: "range",
        filterFn: "customScaledKiloUnitInRange",
        Cell: ({ row }) => {
          const uncurtInvPow = row.original.total_uncurtailable_inverter_power;
          return uncurtInvPow != null && !isNaN(uncurtInvPow) ? `${(uncurtInvPow / 1000).toFixed(2)}kW` : uncurtInvPow;
        },
      },
      {
        accessorKey: "total_controlable_power",
        header: "TOTAL CONTROLABLE POWER",
        enableGlobalFilter: false,
        filterVariant: "range",
        filterFn: "customScaledKiloUnitInRange",
        Cell: ({ row }) => {
          const conPow = row.original.total_controlable_power;
          return conPow != null && !isNaN(conPow) ? `${(conPow / 1000).toFixed(2)}kW` : conPow;
        },
      },
      {
        accessorKey: "has_curtailable_devices",
        header: "HAS CURTAILABLE DEVICES",
        enableGlobalFilter: false,
        filterVariant: "checkbox",
        Cell: ({ cell }) => toUnicodeCheck(cell.getValue()),
      },
      {
        accessorKey: "nb_devices",
        header: "NUMBER OF DEVICES",
        enableGlobalFilter: false,
        filterFn: "inNumberRange",
      },
      {
        accessorFn: (row) => {
          const val = row.act_optimizer?.toLowerCase();
          if (val === "elion" || val === "elion-enris") return "Imbalance";
          if (val === "self-cons") return "Self consumption";
          return row.act_optimizer;
        },
        id: "act_optimizer",
        header: "ACT OPTIMIZER",
      },
      {
        accessorFn: (row) => {
          const val = row.conf_optimizer?.toLowerCase();
          if (val === "elion" || val === "elion-enris") return "Imbalance";
          if (val === "self-cons") return "Self consumption";
          return row.conf_optimizer;
        },
        id: "conf_optimizer",
        header: "CONF OPTIMIZER",
      },
      {
        accessorKey: "capacity",
        header: "CAPACITY",
        enableGlobalFilter: false,
        filterVariant: "range",
        filterFn: "customScaledKiloUnitInRange",
        Cell: ({ row }) => {
          const capacity = row.original.capacity;
          return capacity != null && !isNaN(capacity) ? `${(capacity / 1000).toFixed(2)}kWh` : capacity;
        },
      },
    ],
    [],
  );

  const rowProps = (row) => ({
    onDoubleClick: () => handleTableRowClick(row),
    sx: {
      cursor: "pointer",
    },
  });

  const handleTableRowClick = async (row) => {
    setSiteSelectionSource(SITE_SELECTION_SOURCE.SITES_OVERVIEW);
    sessionStorage.setItem(globalFilterSessionStorageKey, globalFilterValue || "");
    navigate(`${row.original.site_id}/${NAVIGATION_PATH.GENERAL}`, {
      replace: true,
      state: { includeMutes: muteView !== "mutes" },
    });
  };

  const { mutate: newInstallationMutation, isPending } = useMutation({
    mutationFn: async (data) =>
      await createNewInstallation(
        backend.fetch,
        !!data.isDebug,
        data.siteName,
        data.siteId,
        data.boxId,
        data.crmId,
        data.partnerCompanyId,
        data.action,
        data.ean_1,
        data.ean_2,
      ),
    onSuccess: () => {
      Snackbar.success("New site success");
    },
    onError: (err, data, context) => {
      Snackbar.error("New site failed: " + err);
    },
  });

  const handleInstallerDraftActionConfirm = async (selectedRow, dialogAction, dialogData) => {
    newInstallationMutation({
      siteId: dialogData["site_id"],
      siteName: dialogData["site_name"],
      boxId: dialogData.box_id,
      isDebug: !!dialogData.is_debug,
      crmId: selectedRow ? selectedRow.crm_id : null,
      partnerCompanyId: dialogData[FORM_ACTIONS.DRAFT_INSTALLATION_WITH_PARTNER_PRIVILEGE.fnDataAccessKey],
      action: dialogAction.action,
      ean_1: dialogData.ean_1,
      ean_2: dialogData.ean_2,
    });

    setIsDialogOpen(false);
  };

  function handleInstallerDraftInstallationOnClick() {
    setDialogTabs([
      {
        label: "New site",
        component: (
          <DialogForm
            selectedRow={{}} // Pass selected row data
            onClose={() => setIsDialogOpen(false)} // Close the dialog
            dialogAction={FORM_ACTIONS.DRAFT_INSTALLATION} // Your action
            handleConfirm={handleInstallerDraftActionConfirm}
            isPending={isPending}
          />
        ),
      },
    ]);
    setIsDialogOpen(true); // Open the dialog
  }

  function draftInstallationButton() {
    return (
      <Stack direction="row">
        {hasPermission(permissionMap, Privilege.DEVICES_OVERVIEW.getPrivilegeKey(), true) ? (
          <>
            <Tooltip title={TOOLTIPS_EN.NEW_SITE_BUTTON}>
              <Button
                sx={{
                  alignItems: "center",
                  borderRadius: "3px", // Small rounded corners
                  border: "0.5px solid", // Optional: add a border
                  color: "text.secondary",
                  textTransform: "none", // Prevent text from being capitalized
                }}
                aria-label="create draft installation"
                startIcon={<DesignServicesIcon fontSize="small" />} // Adds the icon before text
                onClick={handleInstallerDraftInstallationOnClick}
              >
                New Site
              </Button>
            </Tooltip>
          </>
        ) : null}
      </Stack>
    );
  }

  return (
    <>
      <Box sx={{ flexWrap: "wrap", width: "100%" }}>
        <ReactTableWithSettings
          unique_table_key={REACT_TABLE_NAMES.SITE_OVERVIEW}
          columns={columns}
          data={sanitizedData}
          error={error}
          isLoading={isLoading}
          defaultVisibility={defaultVisibility}
          defaultSorting={defaultSorting}
          customSorting={locationContextSorting}
          rowProps={rowProps}
          defaultFilters={locationContextFilters}
          topToolbarComponent={draftInstallationButton()}
          additionalTopToolbarComponent={toolbarToggleButtons}
          initialGlobalFilter={globalFilterValue}
          onGlobalFilterChangeExternal={handleGlobalFilterChange}
          externalColumnFilters={externalColumnFilters}
          onClearFilters={handleClearFilters}
        />
        <MultiTabDialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} dialogTabs={dialogTabs} />
      </Box>
    </>
  );
}
